import React, { Component } from 'react'
import { findDOMNode } from 'react-dom'
import Highlight from 'react-highlighter'
import { filter } from 'fuzzy'
import uniq from 'lodash.uniq'
import mousetrap from 'mousetrap'

import classNames from './AutoComplete.css'
import { AUTOCOMPLETE_MINIMUM_CHARACTERS, AUTOCOMPLETE_PAGE_SIZE } from '../../constants'
import { matchString, AND_REGEX, AMPERSAND_REGEX } from '../../utils/string'

const PAGINATION_SENSITIVITY = 40 // Higher means more keywords load earlier

export default class AutoComplete extends Component {
  state = {
    limit: AUTOCOMPLETE_PAGE_SIZE,
    matches: [],
    fuzzyMatches: [],
    selected: 0
  }
  componentDidMount () {
    this.node = findDOMNode(this)
    this.node.addEventListener('scroll', this.onScroll)
    mousetrap.bind('up', this.onKeyUp)
    mousetrap.bind('down', this.onKeyDown)
    mousetrap.bind('enter', this.onKeyEnter)
    mousetrap.bind('escape', this.props.onClose)

    const matches = this.getMatches(this.props)
    this.setState({
      matches,
      fuzzyMatches: this.getFuzzyMatches(this.props, matches)
    })
  }
  componentWillUnmount () {
    this.node.removeEventListener('scroll', this.onScroll)
    mousetrap.unbind(['up', 'down', 'enter', 'escape'])
  }
  componentWillReceiveProps (nextProps, nextState) {
    const { text, options } = this.props
    if (text !== nextProps.text || (!options.keywordArray && nextProps.options.keywordArray)) {
      const matches = this.getMatches(nextProps)
      this.setState({
        limit: AUTOCOMPLETE_PAGE_SIZE,
        matches,
        fuzzyMatches: this.getFuzzyMatches(nextProps, matches),
        selected: 0
      })
    }
  }
  onKeyDown = e => {
    const { selected, matches, fuzzyMatches } = this.state
    if (selected < matches.length + fuzzyMatches.length) {
      e.preventDefault()
      this.setState({
        selected: selected + 1
      })
    }
  }
  onKeyUp = e => {
    e.preventDefault()
    if (this.state.selected === 1) {
      this.props.focusInput()
    }
    this.setState({
      selected: Math.max(0, this.state.selected - 1)
    })
  }
  onKeyEnter = e => {
    const { addTerm, text } = this.props
    if (this.state.selected === 0) {
      e.preventDefault()
      addTerm('freetext', text)
    }
  }
  componentDidUpdate (prevProps, prevState) {
    const { selected } = this.state
    if (prevState.selected !== selected && selected !== 0) {
      const button = this.node.querySelectorAll('button')[selected]
      button && button.focus()
    }
  }
  onScroll = () => {
    const { matches, fuzzyMatches, limit } = this.state
    const diff = this.node.scrollHeight - (this.node.scrollTop + this.node.offsetHeight)
    const more = matches.length + fuzzyMatches.length > limit
    if (more && diff <= PAGINATION_SENSITIVITY) {
      this.setState({
        limit: limit + AUTOCOMPLETE_PAGE_SIZE
      })
    }
  }
  shouldDisplayMatches (props) {
    const { text, options } = props
    return options.keywordArray && text.length >= AUTOCOMPLETE_MINIMUM_CHARACTERS
  }
  getMatches (props) {
    const { text, options } = props
    if (!this.shouldDisplayMatches(props)) {
      return []
    }
    const exactMatches = options.keywordArray.filter(item => {
      return matchString(text, item.keyword, true)
    })
    const synMatches = options.keywordArray.filter(item => {
      return item.synonyms && matchString(text, item.synonyms.join(' '))
    })
    return exactMatches.concat(synMatches).filter(this.filterMatches)
  }
  getFuzzyMatches (props, exactMatches) {
    const { text, options } = props
    if (!this.shouldDisplayMatches(props)) {
      return []
    }
    const fuzzyOptions = {
      extract: item => item.keyword
    }
    const matches = filter(text, options.keywordArray, fuzzyOptions)
    return uniq(matches.map(match => match.original))
      .filter(this.filterMatches)
      .filter(item => exactMatches.findIndex(i => i.id === item.id) === -1)
  }
  filterMatches = item => {
    const { exclude } = this.props
    return exclude.indexOf(item.id) === -1
  }
  getTagMatches = text => {
    const { options } = this.props
    if (!this.shouldDisplayMatches(this.props)) {
      return []
    }
    return options.tags.filter(tag => matchString(text, tag))
  }
  renderItem (type, item, text = '', isDuplicate) {
    const { operator, addTerm, focusInput } = this.props
    const { selected } = this.state
    const onClick = () => {
      addTerm(type, item.id, operator)
      focusInput()
    }
    const buttonClass = type === 'freetext' && selected === 0 ? classNames.buttonActive : classNames.button
    const isPrimaryMatch = matchString(text, item.keyword)
    const synMatches = !isPrimaryMatch && item.synonyms && item.synonyms.filter(syn => {
      return matchString(text, syn)
    })
    let highlightText = text || ''
    if (AMPERSAND_REGEX.test(text) && matchString(text.replace(AMPERSAND_REGEX, ' and '), item.keyword)) {
      highlightText = text.replace(AMPERSAND_REGEX, ' and ')
    }
    if (AND_REGEX.test(text) && matchString(text.replace(AND_REGEX, ' & '), item.keyword)) {
      highlightText = text.replace(AND_REGEX, ' & ')
    }
    return (
      <li key={item.id} className={classNames.item}>
        <button className={buttonClass} onClick={onClick}>
          <Highlight search={highlightText} matchElement='span' matchClass={classNames.match}>
            {item.keyword}
          </Highlight>
          {isDuplicate &&
            <span className={classNames.parentNotice}>
              {item.direct_parent_name}
            </span>
          }
          {synMatches && synMatches.length !== 0 &&
            <Highlight className={classNames.parentNotice} search={text || ''} matchElement='span' matchClass={classNames.match}>
              {synMatches[0]}
            </Highlight>
          }
        </button>
      </li>
    )
  }
  render () {
    const { text } = this.props
    const { matches, fuzzyMatches, limit } = this.state
    const limitedMatches = matches.slice(0, limit)
    const limitFuzzy = Math.max(0, limit - limitedMatches.length)
    const limitedFuzzyMatches = limitFuzzy !== 0 ? fuzzyMatches.slice(0, limitFuzzy) : []
    const tagMatches = this.getTagMatches(text)

    return (
      <ul className={classNames.list}>
        <li className={classNames.heading}>
          Free search
        </li>
        {this.renderItem('freetext', { id: text, keyword: text })}
        {limitedMatches.length !== 0 &&
          <li className={classNames.heading}>
            Tags
          </li>
        }
        {tagMatches.map(tag => this.renderItem('tags', { id: tag, keyword: 'Tag: ' + tag }, text))}
        {limitedMatches.map(item => this.renderItem('keyword', item, text, checkDuplicate(item, limitedMatches)))}
        {limitedFuzzyMatches.length !== 0 &&
          <li className={classNames.heading}>
            Did you mean
          </li>
        }
        {limitedFuzzyMatches.map(item => this.renderItem('keyword', item, text, checkDuplicate(item, limitedFuzzyMatches)))}
      </ul>
    )
  }
}

function checkDuplicate (item, matches) {
  for (let match of matches) {
    if (match.keyword === item.keyword && match.id !== item.id) {
      return true
    }
  }
  return false
}



// WEBPACK FOOTER //
// ./src/components/search/AutoComplete.js